import { useState, FormEvent } from 'react';
import { useAuth, UserRole, AuthError } from '../../../hooks/useAuth';

interface Props {
  role: UserRole;
  onSuccess: () => void;
  onBack: () => void;
}

const ERROR_MESSAGES: Record<AuthError, string> = {
  email_exists: 'Ya existe una cuenta con ese correo. Inicie sesión.',
  invalid_credentials: 'Correo o contraseña incorrectos.',
  invalid_ugpp_code: 'El código institucional es incorrecto. Solo los miembros del Comité pueden acceder.',
  user_not_found: 'No se encontró una cuenta registrada con ese correo.',
  role_mismatch: 'Esta cuenta no tiene acceso a este panel.',
  supabase_error: 'Error de servidor. Intente de nuevo más tarde.',
};

type ScreenMode = 'login' | 'register' | 'forgot' | 'reset';

export default function AuthScreen({ role, onSuccess, onBack }: Props) {
  const { register, login, resetPassword, emailExists } = useAuth();
  const [mode, setMode] = useState<ScreenMode>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showNewConfirm, setShowNewConfirm] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [regTerms, setRegTerms] = useState(false);

  const isUgpp = role === 'ugpp';

  const handleForgotSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const email = (fd.get('forgotEmail') as string).trim();
    const exists = await emailExists(email);
    if (!exists) {
      setError('No se encontró una cuenta registrada con ese correo.');
      return;
    }
    setForgotEmail(email);
    setMode('reset');
  };

  const handleResetSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const fd = new FormData(e.currentTarget);
    const newPass = fd.get('newPassword') as string;
    const confirmPass = fd.get('confirmPassword') as string;
    if (newPass.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      setLoading(false);
      return;
    }
    if (newPass !== confirmPass) {
      setError('Las contraseñas no coinciden.');
      setLoading(false);
      return;
    }
    await new Promise(r => setTimeout(r, 400));
    const result = await resetPassword(forgotEmail);
    setLoading(false);
    if (result.success) {
      setResetSuccess(true);
    } else {
      setError(ERROR_MESSAGES[result.error!] || 'Ocurrió un error. Intente de nuevo.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const fd = new FormData(e.currentTarget);
    const email = (fd.get('email') as string).trim();
    const password = fd.get('password') as string;
    const nombre = (fd.get('nombre') as string || '').trim();
    const confirm = fd.get('confirm') as string;
    const ugppCode = (fd.get('ugppCode') as string || '').trim();

    if (mode === 'register') {
      if (!nombre) { setError('El nombre es requerido.'); setLoading(false); return; }
      if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); setLoading(false); return; }
      if (password !== confirm) { setError('Las contraseñas no coinciden.'); setLoading(false); return; }
    }

    await new Promise(r => setTimeout(r, 400));

    let result;
    if (mode === 'register') {
      result = await register(email, password, nombre, role, isUgpp ? ugppCode : undefined);
    } else {
      result = await login(email, password, role, isUgpp ? ugppCode : undefined);
    }

    setLoading(false);

    if (result.success) {
      onSuccess();
    } else {
      setError(ERROR_MESSAGES[result.error!] || 'Ocurrió un error. Intente de nuevo.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />

      {/* Header */}
      <div className="bg-[#003F7D] px-8 py-8" style={{borderBottom: '3px solid #F5A623'}}>
        <div className="max-w-screen-xl mx-auto">
          {/* Back button row */}
          <button
            onClick={onBack}
            className="whitespace-nowrap flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium px-3 py-1.5 rounded-lg border border-white/20 hover:border-white bg-white/10 hover:bg-white/20 transition-all cursor-pointer mb-6"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
            Volver a selección de perfil
          </button>

          <div className="flex items-center gap-5">
            <div className="w-20 h-20 flex items-center justify-center bg-white rounded-xl p-1.5 shadow-md flex-shrink-0">
              <img
                src="https://ugpp.upal.edu/semilla_azul.png"
                alt="Logo UPAL"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <p className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">
                Universidad Privada Abierta Latinoamericana
              </p>
              <h1 className="text-2xl font-bold text-white">Vicerrectorado de Investigación, innovacion y desarrollo institucional</h1>
              <p className="text-white/60 text-sm">Sistema UGPP – Unidad de Gestión de Proyectos y Publicaciones</p>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="bg-white border border-slate-200 rounded-xl p-10 w-full max-w-md relative">

          {/* Back icon button top-left */}
          <button
            onClick={onBack}
            title="Volver a selección de perfil"
            className="absolute top-5 left-5 w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-teal-600 hover:border-teal-300 hover:bg-teal-50 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base" />
          </button>

          {/* Icon + title */}
          <div className="flex items-center justify-center w-14 h-14 bg-teal-50 rounded-xl mb-5 mx-auto">
            <i className={`text-3xl text-teal-600 ${isUgpp ? 'ri-shield-user-line' : 'ri-user-3-line'}`} />
          </div>

          <h2 className="text-xl font-bold text-slate-800 text-center mb-1">
            {isUgpp ? 'UGPP / Comité de Investigación' : 'Portal del Investigador'}
          </h2>
          <p className="text-slate-500 text-sm text-center mb-6">
            {mode === 'login'
              ? 'Inicie sesión para continuar'
              : 'Cree su cuenta para presentar proyectos'}
          </p>

          {/* Mode tabs — solo para login/register */}
          {(mode === 'login' || mode === 'register') && (
            <div className="flex bg-slate-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => { setMode('login'); setError(null); }}
                className={`whitespace-nowrap flex-1 py-2 rounded-md text-sm font-semibold transition-all cursor-pointer ${
                  mode === 'login' ? 'bg-white text-teal-700' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Iniciar sesión
              </button>
              <button
                type="button"
                onClick={() => { setMode('register'); setError(null); }}
                className={`whitespace-nowrap flex-1 py-2 rounded-md text-sm font-semibold transition-all cursor-pointer ${
                  mode === 'register' ? 'bg-white text-teal-700' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Registrarse
              </button>
            </div>
          )}

          {/* ── PANTALLA: Olvidé mi contraseña ── */}
          {mode === 'forgot' && (
            <>
              <div className="flex items-center gap-2 mb-5">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(null); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-teal-600 hover:border-teal-300 hover:bg-teal-50 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-sm" />
                </button>
                <span className="text-sm font-semibold text-slate-700">Recuperar contraseña</span>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                <p className="text-xs text-amber-700 flex items-start gap-2">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-information-line" /></span>
                  Ingrese el correo con el que se registró. Si existe una cuenta, recibirá un enlace por correo para restablecer su contraseña.
                </p>
              </div>
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Correo electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="forgotEmail"
                    type="email"
                    required
                    placeholder="correo@universidad.edu"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                {error && (
                  <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-error-warning-line" /></span>
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  className="whitespace-nowrap w-full bg-[#00426A] hover:bg-[#02ACB5] text-white py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-send-line" /></span>
                  Verificar correo y continuar
                </button>
              </form>
            </>
          )}

          {/* ── PANTALLA: Restablecer contraseña ── */}
          {mode === 'reset' && (
            <>
              {resetSuccess ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-4xl text-teal-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">Contraseña actualizada</h3>
                  <p className="text-sm text-slate-500 mb-6">Su contraseña fue cambiada exitosamente. Ya puede iniciar sesión con la nueva contraseña.</p>
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(null); setResetSuccess(false); setForgotEmail(''); }}
                    className="whitespace-nowrap w-full bg-[#00426A] hover:bg-[#02ACB5] text-white py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-login-box-line" /></span>
                    Ir a iniciar sesión
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-5">
                    <button
                      type="button"
                      onClick={() => { setMode('forgot'); setError(null); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-teal-600 hover:border-teal-300 hover:bg-teal-50 transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-sm" />
                    </button>
                    <span className="text-sm font-semibold text-slate-700">Nueva contraseña</span>
                  </div>
                  <div className="bg-teal-50 border border-teal-100 rounded-lg px-4 py-3 mb-4">
                    <p className="text-xs text-teal-700 flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-user-line" /></span>
                      Cuenta: <strong className="ml-1">{forgotEmail}</strong>
                    </p>
                  </div>
                  <form onSubmit={handleResetSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        Nueva contraseña <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          name="newPassword"
                          type={showNewPass ? 'text' : 'password'}
                          required
                          placeholder="Mínimo 6 caracteres"
                          className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPass(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer"
                        >
                          <i className={showNewPass ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        Confirmar nueva contraseña <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          name="confirmPassword"
                          type={showNewConfirm ? 'text' : 'password'}
                          required
                          placeholder="Repita la nueva contraseña"
                          className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewConfirm(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer"
                        >
                          <i className={showNewConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </button>
                      </div>
                    </div>
                    {error && (
                      <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-error-warning-line" /></span>
                        {error}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="whitespace-nowrap w-full bg-[#00426A] hover:bg-[#02ACB5] disabled:opacity-60 text-white py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin" /></span>Guardando...</>
                      ) : (
                        <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-password-line" /></span>Guardar nueva contraseña</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* ── FORMULARIO: Login / Registro ── */}
          {(mode === 'login' || mode === 'register') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nombre (solo registro) */}
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <input
                  name="nombre"
                  type="text"
                  required
                  placeholder="Ej.: Dr. Juan Pérez"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Correo electrónico <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                type="email"
                required
                placeholder="correo@universidad.edu"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  name="password"
                  type={showPass ? 'text' : 'password'}
                  required
                  placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : 'Su contraseña'}
                  className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer"
                >
                  <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            {/* Confirm password (solo registro) */}
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Confirmar contraseña <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    name="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    required
                    placeholder="Repita su contraseña"
                    className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer"
                  >
                    <i className={showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
                  </button>
                </div>
              </div>
            )}

            {/* Código UGPP */}
            {isUgpp && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Código institucional UGPP <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    name="ugppCode"
                    type={showCode ? 'text' : 'password'}
                    required
                    placeholder="Código asignado al Comité"
                    className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCode(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer"
                  >
                    <i className={showCode ? 'ri-eye-off-line' : 'ri-eye-line'} />
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-shield-keyhole-line" /></span>
                  El código institucional es el mismo que su contraseña de acceso.
                </p>
              </div>
            )}

            {mode != 'login'? (
            <div className="items-start gap-3 bg-[#0D3157]/5 border border-[#0D3157]/15 rounded-lg px-4 py-3 mb-1">
              <span className="text-[#0D3157]">Consentimiento informado</span>
              
                <p className="text-[#0D3157] text-xs leading-relaxed">
                  Declaro haber leído y comprendido que la información registrada en la Aplicación 
                  de gestión de proyectos será utilizada <b>exclusivamente con fines institucionales 
                  de planificación, seguimiento y fortalecimiento de la investigación</b>, bajo administración 
                  del Vicerrectorado de Investigación, Innovación y Desarrollo Institucional y del Comité 
                  de Investigación e Interacción. 
                </p>
                <p className="pt-2 text-[#0D3157] text-xs leading-relaxed">
                  Asimismo, declaro que <b>la información consignada sobre 
                  mis publicaciones e investigaciones es veraz y respeta los principios de integridad 
                  académica</b>, incluyendo la adecuada citación de fuentes y la prohibición del plagio o la 
                  falsificación de resultados.
                </p>
                
                {regTerms ? (
                  <p className="pt-3 text-xs text-[#0D3157] text-slate-400">
                    <i className="ri-check-fill text-[#0C8D2F] text-sm mt-0.5 shrink-0" />
                    <span className="text-[#0D3157]"> Estoy de acuerdo</span>
                  </p>
                ): (
                  <p className="pt-3 text-xs text-slate-400">
                    <button
                      onClick={() => setRegTerms(true)}
                      type="button"
                      className="text-[#0D3157] font-semibold hover:underline cursor-pointer"
                    >
                    <i className="ri-checkbox-blank-line"></i>
                    <span> Estoy de acuerdo</span>
                    </button>
                  </p>
                )
                }    
            </div>
            ): (<div></div>)}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line" />
                </span>
                {error}
              </div>
            )}

            {/* Olvidé contraseña — solo en login de investigador */}
            {mode === 'login' && !isUgpp && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(null); }}
                  className="text-xs text-teal-600 hover:text-teal-800 hover:underline font-medium cursor-pointer"
                >
                  ¿Olvidó su contraseña?
                </button>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || (mode!='login' && !regTerms)}
              className="whitespace-nowrap w-full bg-[#00426A] hover:bg-[#02ACB5] disabled:opacity-60 text-white py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin" /></span>
                  Procesando...
                </>
              ) : mode === 'login' ? (
                <>
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-login-box-line" /></span>
                  Iniciar sesión
                </>
              ) : (
                <>
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line" /></span>
                  Crear cuenta
                </>
              )}
            </button>
          </form>
          )}

          {/* Back link */}
          <p className="text-center text-xs text-slate-400 mt-4">
            ¿Equivocó de perfil?{' '}
            <button
              type="button"
              onClick={onBack}
              className="text-teal-600 hover:underline font-semibold cursor-pointer"
            >
              Volver a la selección
            </button>
          </p>

          {/* Info box */}
          {!isUgpp && (
            <div className="mt-5 bg-teal-50 border border-teal-100 rounded-lg p-3">
              <p className="text-xs font-semibold text-teal-700 mb-1 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line" /></span>
                ¿Para qué sirve la cuenta?
              </p>
              <p className="text-xs text-teal-600">
                Con su cuenta podrá presentar proyectos, ver el código asignado, subir documentos y recibir notificaciones del estado de evaluación del Comité.
              </p>
            </div>
          )}
        </div>
      </div>

      <footer className="bg-[#002F6C] text-white/60 py-5">
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-center">
          <p className="text-xs">UGPP – Vicerrectorado de Investigación · Sistema de Gestión de Proyectos y Publicaciones · 2026</p>
        </div>
      </footer>
    </div>
  );
}